import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useAppSelector } from '../hooks';
import useAuth from '../components/Auth';
import { selectIsAuthenticated } from './authSlice';
import { selectAllEntries, selectAllRepositories, selectIsEntriesHydrated } from './contentSlice';
import { useLoadEntryQuery } from '../services/records';
import { useLoadRepositoriesQuery } from '../services/repositoryEntries';
import { buildCollectionUrlWithFilters } from '../common/util';
import { NOTE_REPOSITORY } from '../systemRecords/note';
import { REPOSITORY_REPOSITORY } from '../systemRecords/repository';
import { EntityType, PressedKey, type ChartEntry } from '../types';
import { isNoAccessQueryError, isNotFoundQueryError } from '../common/queryErrors';
import ChartDetail from '../components/ChartDetail';
import EditChartDialog from '../components/EditChartDialog';
import FabAreaWrapperBottomRight from '../components/FabAreaWrapperBottomRight';
import { ActionAreaFab } from '../components/ActionAreaFab';
import { useChartEntryUpdate } from '../hooks/useChartEntryUpdate';
import { useKeyboardEffects } from '../components/KeyboardEffects';
import NotFoundView from './NotFoundView';
import NoAccessView from './NoAccessView';

function ChartView() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { isAuthenticating } = useAuth();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const allEntries = useAppSelector(selectAllEntries);
	const allRepositories = useAppSelector(selectAllRepositories);
	const chart = useMemo(
		() => allEntries.find((e) => e.id === id && e.type === EntityType.Chart) as ChartEntry | undefined,
		[allEntries, id],
	);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const updateChartEntry = useChartEntryUpdate();

	const setKeyboardMappings = useKeyboardEffects();
	const handleKeyPressEscape = useCallback(() => {
		if (isEditDialogOpen) return;
		navigate(chart?.parentId ? `/${chart.parentId}` : '/');
	}, [navigate, isEditDialogOpen, chart?.parentId]);
	useEffect(() => {
		setKeyboardMappings({
			[PressedKey.Escape]: handleKeyPressEscape,
		});
		return () => setKeyboardMappings(null);
	}, [handleKeyPressEscape, setKeyboardMappings]);

	const { data: chartRecords, isSuccess: isChartRecordLoaded, error: chartError } = useLoadEntryQuery(id ?? null, {
		skip: !id || isAuthenticating || !isAuthenticated,
	});
	const { data: remoteRepositories = [], isSuccess: isRemoteRepositoriesLoaded } = useLoadRepositoriesQuery(undefined, {
		skip: !isAuthenticated,
	});
	const repositories = useMemo(
		() => (isRemoteRepositoriesLoaded ? remoteRepositories : allRepositories).filter(
			(repository) => ![NOTE_REPOSITORY.id, REPOSITORY_REPOSITORY.id].includes(repository.id),
		),
		[isRemoteRepositoriesLoaded, remoteRepositories, allRepositories],
	);

	if (isNoAccessQueryError(chartError)) {
		return <NoAccessView />;
	}
	/** `records/:id` answers an unknown or inaccessible id with an empty record rather than an error. */
	const isChartRecordMissing = isChartRecordLoaded && !chartRecords?.[0];
	const isMissingFromLocalData = !isAuthenticated && !isAuthenticating && isEntriesHydrated && !chart;
	if (isChartRecordMissing || isMissingFromLocalData || isNotFoundQueryError(chartError)) {
		return <NotFoundView />;
	}
	if (!chart) {
		return (
			<Box flex={1} display="flex" justifyContent="center" alignItems="center">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box display="flex" flexDirection="column" minHeight="100dvh">
			<ChartDetail chart={chart} />
			<FabAreaWrapperBottomRight>
				<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
					<ActionAreaFab
						size="medium"
						aria-label="Open list view"
						onClick={() => navigate(buildCollectionUrlWithFilters(chart.repositoryId, chart.where, chart.includeArchived))}
					>
						<ViewListIcon />
					</ActionAreaFab>
					<ActionAreaFab size="large" color="primary" aria-label="Edit chart" onClick={() => setIsEditDialogOpen(true)}>
						<EditIcon />
					</ActionAreaFab>
				</Box>
			</FabAreaWrapperBottomRight>
			<EditChartDialog
				open={isEditDialogOpen}
				chart={chart}
				repositories={repositories}
				onClose={() => setIsEditDialogOpen(false)}
				onSubmit={(payload) => {
					updateChartEntry(payload);
					setIsEditDialogOpen(false);
				}}
			/>
		</Box>
	);
}

export default ChartView;
